import { HTMLAttributes, useCallback } from 'react'

import { Track } from 'livekit-client'
import { log } from '@livekit/components-core'

import { ControlBar } from './ControlBar'

import { TrackLoop } from '@/src/components'
import { ParticipantAudioTile } from '@/src/components/participant/ParticipantAudioTile'
import { Chat } from '@/src/components/room/Chat/Chat'

import { LayoutContextProvider } from '@/src/layout/LayoutContextProvider'

import { useLayoutContext } from '@/src/context'

import type { WidgetState } from '@/src/lib/types'

import { useTracks } from '@/src/hooks'
import { useWarnAboutMissingStyles } from '@/src/hooks/useWarnAboutMissingStyles'

/** @public */
export interface AudioConferenceProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * This component is the default setup of a classic LiveKit audio conferencing app.
 * It provides functionality like switching between participant grid view and focus view.
 *
 * @remarks
 * The component is implemented with other LiveKit components like `FocusContextProvider`,
 * `GridLayout`, `ControlBar`, `FocusLayoutContainer` and `FocusLayout`.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <AudioConference />
 * <LiveKitRoom>
 * ```
 * @public
 */

const controlsOfControlBar = { microphone: true, screenShare: false, camera: false, chat: true }
const tracks = [Track.Source.Microphone]

export function AudioConference({ ...props }: AudioConferenceProps) {
  const audioTracks = useTracks(tracks)
  const { dispatch } = useLayoutContext().widget

  useWarnAboutMissingStyles()

  const widgetUpdate = useCallback((state: WidgetState) => {
    log.debug('updating widget state', state)
    dispatch({ msg: 'set_widget_state', widget: state })
  }, [])

  return (
    <LayoutContextProvider onWidgetChange={widgetUpdate}>
      <div className="lk-audio-conference" {...props}>
        <div className="lk-audio-conference-stage">
          <TrackLoop tracks={audioTracks}>
            <ParticipantAudioTile />
          </TrackLoop>
        </div>
        <ControlBar controls={controlsOfControlBar} />

        <Chat />
      </div>
    </LayoutContextProvider>
  )
}
